import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Table,
  Button,
  Alert,
  Spinner,
  Card,
  Badge,
  Modal,
  Form,
  Row,
  Col,
  Tabs,
  Tab,
} from "react-bootstrap";
import {
  FaUsers,
  FaTrash,
  FaSignOutAlt,
  FaDatabase,
  FaCalendarPlus,
  FaCalendarCheck,
  FaClock,
  FaEdit,
  FaUser,
  FaBuilding,
} from "react-icons/fa";

const API_URL = process.env.REACT_APP_API_URL;

function AdminDashboard() {
  const [users, setUsers] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [deleteSuccess, setDeleteSuccess] = useState("");
  const navigate = useNavigate();

  // Subscription form state
  const [subscriptionForm, setSubscriptionForm] = useState({
    user_id: "",
    start_date: new Date().toISOString().split("T")[0],
    end_date: "",
    subscription_type: "monthly",
    notes: "",
  });

  // Check admin authentication
  useEffect(() => {
    const isAdmin = localStorage.getItem("adminAuthenticated");
    if (!isAdmin || isAdmin !== "true") {
      navigate("/login");
      return;
    }
    fetchAllData();
  }, [navigate]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([fetchUsers(), fetchSubscriptions()]);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/admin/users`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch users");
      }

      const data = await response.json();
      setUsers(data.users || []);
    } catch (err) {
      setError(err.message);
    }
  };

  const fetchSubscriptions = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/admin/subscriptions`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch subscriptions");
      }

      const data = await response.json();
      setSubscriptions(data.subscriptions || []);
    } catch (err) {
      console.error("Error fetching subscriptions:", err);
      setError("Failed to fetch subscriptions");
    }
  };

  const handleDeleteOldSummaries = async () => {
    setDeleting(true);
    setError("");
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/admin/delete-old-summaries`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to delete summaries");
      }

      setDeleteSuccess(
        `Successfully deleted ${data.deletedCount} sales summaries older than 30 days`,
      );
      setShowDeleteModal(false);

      setTimeout(() => setDeleteSuccess(""), 5000);
    } catch (err) {
      setError(err.message);
    } finally {
      setDeleting(false);
    }
  };

  const handleSubscriptionSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/admin/subscriptions`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(subscriptionForm),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to manage subscription");
      }

      setSuccess(data.message || "Subscription managed successfully");
      setShowSubscriptionModal(false);
      resetForm();
      await fetchSubscriptions(); // Refresh the list

      setTimeout(() => setSuccess(""), 5000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditSubscription = (subscription) => {
    setSelectedSubscription(subscription);
    setSubscriptionForm({
      user_id: subscription.user_id?._id || subscription.user_id,
      start_date: new Date(subscription.start_date).toISOString().split("T")[0],
      end_date: new Date(subscription.end_date).toISOString().split("T")[0],
      subscription_type: subscription.subscription_type || "monthly",
      notes: subscription.notes || "",
    });
    setShowSubscriptionModal(true);
  };

  const resetForm = () => {
    setSubscriptionForm({
      user_id: "",
      start_date: new Date().toISOString().split("T")[0],
      end_date: "",
      subscription_type: "monthly",
      notes: "",
    });
    setSelectedSubscription(null);
  };

  const getSubscriptionStatusBadge = (subscription) => {
    const isExpired = new Date(subscription.end_date) < new Date();
    if (!subscription.is_active) {
      return <Badge bg="secondary">Inactive</Badge>;
    }
    if (isExpired) {
      return <Badge bg="danger">Expired</Badge>;
    }
    return <Badge bg="success">Active</Badge>;
  };

  const getDaysRemaining = (endDate) => {
    const diff = new Date(endDate) - new Date();
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  };

  const handleAdminLogout = () => {
    localStorage.removeItem("adminAuthenticated");
    localStorage.removeItem("adminLoginTime");
    navigate("/login");
  };

  if (loading) {
    return (
      <Container
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "100vh" }}
      >
        <Spinner animation="border" variant="primary" />
      </Container>
    );
  }

  return (
    <div style={{ background: "#f8f9fa", minHeight: "100vh" }}>
      {/* Header */}
      <div
        style={{
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          padding: "20px 0",
          marginBottom: "30px",
        }}
      >
        <Container>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h2 style={{ color: "white", margin: 0 }}>Admin Dashboard</h2>
              <p style={{ color: "rgba(255,255,255,0.8)", margin: 0 }}>
                System Management Console
              </p>
            </div>
            <Button variant="light" onClick={handleAdminLogout}>
              <FaSignOutAlt /> Logout
            </Button>
          </div>
        </Container>
      </div>

      <Container>
        {error && (
          <Alert variant="danger" onClose={() => setError("")} dismissible>
            {error}
          </Alert>
        )}

        {success && (
          <Alert variant="success" onClose={() => setSuccess("")} dismissible>
            {success}
          </Alert>
        )}

        {deleteSuccess && (
          <Alert
            variant="success"
            onClose={() => setDeleteSuccess("")}
            dismissible
          >
            {deleteSuccess}
          </Alert>
        )}

        <Tabs defaultActiveKey="users" className="mb-4">
          <Tab eventKey="users" title="Users Management">
            {/* Users List Card */}
            <Card className="shadow-sm">
              <Card.Header
                style={{
                  background: "#fff",
                  borderBottom: "2px solid #667eea",
                }}
              >
                <h5 className="mb-0">
                  <FaUsers className="me-2" style={{ color: "#667eea" }} />
                  System Users ({users.length})
                </h5>
              </Card.Header>
              <Card.Body>
                <div className="table-responsive">
                  <Table striped hover className="mb-0">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Username</th>
                        <th>Full Name</th>
                        <th>Email</th>
                        <th>Branch ID</th>
                        <th>Branch Name</th>
                        <th>Role</th>
                        <th>Status</th>

                        <th>Created At</th>
                        <th>Last Login</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.length === 0 ? (
                        <tr>
                          <td colSpan="10" className="text-center text-muted">
                            No users found
                          </td>
                        </tr>
                      ) : (
                        users.map((user, index) => (
                          <tr key={user._id}>
                            <td>{index + 1}</td>
                            <td>
                              <strong>{user.username}</strong>
                            </td>
                            <td>{user.full_name || "-"}</td>
                            <td>{user.email || "-"}</td>
                            <td>
                              <Badge bg="secondary">
                                {user.branch_id || "-"}
                              </Badge>
                            </td>
                            <td>
                              <Badge bg="info">{user.branch_name || "-"}</Badge>
                            </td>
                            <td>
                              <Badge
                                bg={
                                  user.role === "admin"
                                    ? "danger"
                                    : user.role === "manager"
                                      ? "warning"
                                      : user.role === "user"
                                        ? "primary"
                                        : "secondary"
                                }
                              >
                                {user.role}
                              </Badge>
                            </td>
                            <td>
                              <Badge bg={user.is_active ? "success" : "danger"}>
                                {user.is_active ? "Active" : "Inactive"}
                              </Badge>
                            </td>

                            <td>
                              {new Date(user.created_at).toLocaleDateString()}
                            </td>
                            <td>
                              {user.last_login
                                ? new Date(user.last_login).toLocaleDateString()
                                : "-"}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>
          </Tab>

          <Tab eventKey="subscriptions" title="Subscription Management">
            {/* Subscription Management Card */}
            <Card className="mb-4 shadow-sm">
              <Card.Header
                style={{
                  background: "#fff",
                  borderBottom: "2px solid #667eea",
                }}
              >
                <div className="d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">
                    <FaCalendarPlus
                      className="me-2"
                      style={{ color: "#667eea" }}
                    />
                    Subscription Management
                  </h5>
                  <Button
                    variant="primary"
                    onClick={() => {
                      resetForm();
                      setShowSubscriptionModal(true);
                    }}
                  >
                    <FaCalendarPlus className="me-2" />
                    Add/Manage Subscription
                  </Button>
                </div>
              </Card.Header>
              <Card.Body>
                <div className="table-responsive">
                  <Table striped hover className="mb-0">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>User</th>
                        <th>Branch ID</th>
                        <th>Start Date</th>
                        <th>End Date</th>
                        <th>Days Remaining</th>
                        <th>Status</th>
                        <th>Type</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {subscriptions.length === 0 ? (
                        <tr>
                          <td colSpan="10" className="text-center text-muted">
                            No subscriptions found
                          </td>
                        </tr>
                      ) : (
                        subscriptions.map((sub, index) => {
                          const daysRemaining = getDaysRemaining(sub.end_date);
                          return (
                            <tr key={sub._id}>
                              <td>{index + 1}</td>
                              <td>
                                <strong>
                                  {sub.user_id?.full_name ||
                                    sub.user_id?.username ||
                                    "N/A"}
                                </strong>
                                <br />
                                <small className="text-muted">
                                  {sub.user_id?.email || ""}
                                </small>
                              </td>
                              <td>
                                <Badge bg="secondary">
                                  {sub.user_id?.branch_id ||
                                    sub.branch_id ||
                                    "N/A"}
                                </Badge>
                              </td>

                              <td>
                                {new Date(sub.start_date).toLocaleDateString()}
                              </td>
                              <td>
                                {new Date(sub.end_date).toLocaleDateString()}
                              </td>
                              <td>
                                {sub.is_active ? (
                                  <Badge
                                    bg={
                                      daysRemaining > 30
                                        ? "success"
                                        : daysRemaining > 7
                                          ? "warning"
                                          : "danger"
                                    }
                                  >
                                    <FaClock className="me-1" />
                                    {daysRemaining > 0
                                      ? `${daysRemaining} days`
                                      : "Expired"}
                                  </Badge>
                                ) : (
                                  <Badge bg="secondary">Inactive</Badge>
                                )}
                              </td>
                              <td>{getSubscriptionStatusBadge(sub)}</td>
                              <td>
                                <Badge bg="info">
                                  {sub.subscription_type || "monthly"}
                                </Badge>
                              </td>
                              <td>
                                {sub.is_active && (
                                  <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={() => handleEditSubscription(sub)}
                                    title="Edit/Renew Subscription"
                                  >
                                    <FaEdit />
                                  </Button>
                                )}
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>

            {/* Database Management Card */}
            <Card className="shadow-sm">
              <Card.Header
                style={{
                  background: "#fff",
                  borderBottom: "2px solid #667eea",
                }}
              >
                <h5 className="mb-0">
                  <FaDatabase className="me-2" style={{ color: "#667eea" }} />
                  Database Management
                </h5>
              </Card.Header>
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center flex-wrap">
                  <div>
                    <h6>Sales Summaries Cleanup</h6>
                    <p className="text-muted mb-0">
                      Delete all sales summaries older than 30 days from the
                      database. This action cannot be undone.
                    </p>
                  </div>
                  <Button
                    variant="danger"
                    onClick={() => setShowDeleteModal(true)}
                    className="mt-2 mt-md-0"
                  >
                    <FaTrash className="me-2" />
                    Delete Old Summaries (&gt;30 days)
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Tab>
        </Tabs>
      </Container>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Are you sure you want to delete all sales summaries older than 30
            days?
          </p>
          <p className="text-danger mb-0">
            <strong>Warning:</strong> This action is permanent and cannot be
            undone.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDeleteOldSummaries}
            disabled={deleting}
          >
            {deleting ? (
              <>
                <Spinner size="sm" animation="border" className="me-2" />
                Deleting...
              </>
            ) : (
              "Yes, Delete All"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Subscription Management Modal */}
      <Modal
        show={showSubscriptionModal}
        onHide={() => {
          setShowSubscriptionModal(false);
          resetForm();
        }}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {selectedSubscription
              ? "Renew Subscription"
              : "Add New Subscription"}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubscriptionSubmit}>
          <Modal.Body>
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Select User *</Form.Label>
                  <Form.Select
                    value={subscriptionForm.user_id}
                    onChange={(e) => {
                      const selectedUser = users.find(
                        (u) => u._id === e.target.value,
                      );
                      setSubscriptionForm({
                        ...subscriptionForm,
                        user_id: e.target.value,
                      });
                    }}
                    required
                    disabled={!!selectedSubscription}
                  >
                    <option value="">Select User</option>
                    {users
                      .filter((user) => user.is_active !== false)
                      .map((user) => (
                        <option key={user._id} value={user._id}>
                          {user.full_name || user.username}
                          {user.branch_id && ` - ID: ${user.branch_id}`}
                          {user.branch_name && ` (${user.branch_name})`}
                          {user.email && ` - ${user.email}`}
                        </option>
                      ))}
                  </Form.Select>
                  {subscriptionForm.user_id && (
                    <div className="mt-2">
                      <Badge bg="info" className="me-2">
                        <FaBuilding className="me-1" />
                        Branch:{" "}
                        {users.find((u) => u._id === subscriptionForm.user_id)
                          ?.branch_name || "N/A"}
                      </Badge>
                      <Badge bg="secondary">
                        <FaUser className="me-1" />
                        ID:{" "}
                        {users.find((u) => u._id === subscriptionForm.user_id)
                          ?.branch_id || "N/A"}
                      </Badge>
                    </div>
                  )}
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Subscription Type</Form.Label>
                  <Form.Select
                    value={subscriptionForm.subscription_type}
                    onChange={(e) =>
                      setSubscriptionForm({
                        ...subscriptionForm,
                        subscription_type: e.target.value,
                      })
                    }
                  >
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly</option>
                    <option value="yearly">Yearly</option>
                    <option value="custom">Custom</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Start Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={subscriptionForm.start_date}
                    onChange={(e) =>
                      setSubscriptionForm({
                        ...subscriptionForm,
                        start_date: e.target.value,
                      })
                    }
                    required
                  />
                  <Form.Text className="text-muted">
                    Default is today's date
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>End Date *</Form.Label>
                  <Form.Control
                    type="date"
                    value={subscriptionForm.end_date}
                    onChange={(e) =>
                      setSubscriptionForm({
                        ...subscriptionForm,
                        end_date: e.target.value,
                      })
                    }
                    required
                    min={subscriptionForm.start_date}
                  />
                  <Form.Text className="text-muted">
                    Must be after start date
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Notes</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    placeholder="Add any notes about this subscription"
                    value={subscriptionForm.notes}
                    onChange={(e) =>
                      setSubscriptionForm({
                        ...subscriptionForm,
                        notes: e.target.value,
                      })
                    }
                  />
                </Form.Group>
              </Col>
            </Row>

            {selectedSubscription && (
              <Alert variant="info">
                <FaCalendarCheck className="me-2" />
                This will <strong>renew</strong> the existing subscription for:
                <br />
                <strong>
                  {selectedSubscription.user_id?.full_name ||
                    selectedSubscription.user_id?.username}
                </strong>
                {selectedSubscription.user_id?.branch_name && (
                  <>
                    {" "}
                    at{" "}
                    <strong>{selectedSubscription.user_id.branch_name}</strong>
                  </>
                )}
                {selectedSubscription.user_id?.branch_id && (
                  <> (ID: {selectedSubscription.user_id.branch_id})</>
                )}
                <br />
                <small className="text-muted">
                  Previous end date:{" "}
                  {new Date(selectedSubscription.end_date).toLocaleDateString()}
                </small>
              </Alert>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => {
                setShowSubscriptionModal(false);
                resetForm();
              }}
            >
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={submitting}>
              {submitting ? (
                <>
                  <Spinner size="sm" animation="border" className="me-2" />
                  {selectedSubscription ? "Renewing..." : "Creating..."}
                </>
              ) : (
                <>
                  {selectedSubscription
                    ? "Renew Subscription"
                    : "Create Subscription"}
                </>
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
}

export default AdminDashboard;
